<template>
	<div ref="root" class="card">
		<div class="card-body">
			<h2 class="h5 mb-0">Author an Assignment</h2>
			<p class="text-muted small mb-2">
				What the work is, who it is for, when it may happen and where. Either
				pick targets on the map — one Assignment is created per target — or turn
				on the bulk door below and author one assignment per checked Work row.
				Everything saved here is born <em>draft</em>: it reaches a technician
				through a Route, and it completes only once the kind's evidence is
				recorded.
			</p>

			<!-- A trap's details and a station's page open this form pre-filled.
			     Saying so is what keeps decision 6 visible: they suggest, the
			     operator authors, and nothing exists until the submit below. -->
			<div v-if="prefillNote" class="alert alert-info py-2 small" role="status">
				{{ prefillNote }}
			</div>

			<!-- `novalidate`: `validationError()` is the form's one validator, and
			     it reports through the alert above the button. The browser's own
			     interactive validation must not run, because the id inputs behind
			     "State an id instead" are always in the DOM, hold their refs'
			     zero until touched, and sit inside a closed <details> — an invalid
			     control there is not focusable, so the browser would abort the
			     submit with nothing said. -->
			<form novalidate @submit.prevent="submit">
				<!-- The bulk door: every checked Work row becomes its own
				     assignment, sharing the fields stated below. It is off by
				     default, because checking rows in the ledger to read them must
				     not change what the form will write. -->
				<div class="form-check form-switch mt-2">
					<input
						id="assignmentBulk"
						class="form-check-input"
						type="checkbox"
						role="switch"
						:checked="bulk"
						:disabled="eligibleWork.length === 0"
						@change="setBulk(($event.target as HTMLInputElement).checked)"
					/>
					<label class="form-check-label small" for="assignmentBulk">
						One assignment per checked Work row
						<span v-if="eligibleWork.length > 0" class="text-muted">
							({{ eligibleWork.length }} checked)
						</span>
					</label>
					<p class="form-text mb-0">
						<template v-if="eligibleWork.length === 0">
							Check Work rows in the ledger to author an assignment for each of
							them here.
						</template>
						<template v-else>
							Each row's whole H3 footprint becomes that assignment's target,
							and the kind, technician and window below are shared by all of
							them.
						</template>
					</p>
				</div>

				<!-- In bulk mode the rows ARE the targets, so the map's picking has
				     nothing to do; the list states what each row will become and
				     names any row that cannot become one. -->
				<fieldset v-if="bulk" class="mt-2">
					<legend class="form-label fs-6">Rows to assign</legend>
					<p v-if="bulkReading" class="text-muted small mb-0">
						Reading each row's H3 footprint…
					</p>
					<p v-else-if="bulkError" class="small text-danger mb-0">
						{{ bulkError }}
					</p>
					<ul v-else class="list-unstyled mb-0">
						<li
							v-for="target in bulkTargets"
							:key="target.row.id"
							class="small py-1 border-bottom"
						>
							<span
								class="badge me-1"
								:class="originBadge(target.row.origin)"
								>{{ target.row.origin }}</span
							>
							{{ target.row.title }}
						</li>
						<li
							v-for="refusal in bulkRefusals"
							:key="refusal.row.id"
							class="small py-1 border-bottom text-danger"
						>
							<span
								class="badge me-1"
								:class="originBadge(refusal.row.origin)"
								>{{ refusal.row.origin }}</span
							>
							{{ refusal.row.title }} — {{ refusal.reason }}
						</li>
					</ul>
				</fieldset>

				<!-- The target is a place on the map, not a radio button naming a
				     field: the operator clicks the Sites, trap stations and parcels
				     the work concerns, or draws the point or area it concerns. Each
				     picked target becomes its own Assignment, so a day's round of
				     visits is authored once and lands as the rows it is. -->
				<fieldset v-else class="mt-2">
					<legend
						class="form-label fs-6 d-flex flex-wrap align-items-center gap-2 w-100"
					>
						<span class="me-auto">Targets</span>
						<span
							class="btn-group btn-group-sm"
							role="group"
							aria-label="How to pick a target"
						>
							<button
								v-for="mode in PICK_MODES"
								:key="mode.value"
								type="button"
								class="btn"
								:class="
									pickMode === mode.value
										? 'btn-primary'
										: 'btn-outline-primary'
								"
								:aria-pressed="pickMode === mode.value"
								:title="mode.hint"
								@click="setPickMode(mode.value)"
							>
								<i class="bi me-1" :class="mode.icon"></i>{{ mode.label }}
							</button>
						</span>
					</legend>

					<p class="form-text mb-2">{{ pickHint }}</p>

					<div v-if="targets.length > 0" class="d-flex flex-wrap gap-2">
						<span
							v-for="(target, index) in targets"
							:key="`${target.kind}-${index}`"
							class="badge rounded-pill text-bg-light border d-inline-flex align-items-center gap-2 fw-normal py-2 target-chip"
						>
							<i class="bi" :class="targetIcon(target)"></i>
							<span>{{ targetText(target) }}</span>
							<button
								type="button"
								class="btn-close target-chip-remove"
								:aria-label="`Remove ${targetText(target)}`"
								@click="removeTarget(index)"
							></button>
						</span>
					</div>
					<p v-else class="text-muted small mb-0">
						No targets picked yet. A second point or area replaces the one
						before it; picked Sites and parcels accumulate.
					</p>
				</fieldset>

				<!-- Two columns and then full rows below: the panel is a narrow
				     column, so a grid that only breaks at `md` would keep all four
				     fields side by side in a viewport that is wide while the panel
				     is not, crushing a `datetime-local` to unreadable width. -->
				<div class="row g-2 mt-1">
					<div class="col-6">
						<label class="form-label" for="assignmentKind">Kind</label>
						<select
							id="assignmentKind"
							v-model="kind"
							class="form-select form-select-sm"
						>
							<option
								v-for="option in KIND_OPTIONS"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
					<div class="col-6">
						<label class="form-label" for="assignmentTechnician">
							Technician
						</label>
						<select
							id="assignmentTechnician"
							v-model.number="technicianId"
							class="form-select form-select-sm"
						>
							<option :value="0" disabled>Choose a technician…</option>
							<option
								v-for="user in technicians"
								:key="user.id"
								:value="user.id"
							>
								{{ user.display_name }}
							</option>
						</select>
					</div>
					<div class="col-12">
						<label class="form-label" for="assignmentStart">Start</label>
						<input
							id="assignmentStart"
							v-model="windowStart"
							type="datetime-local"
							class="form-control form-control-sm"
						/>
					</div>
					<div class="col-12">
						<label class="form-label" for="assignmentEnd">End</label>
						<input
							id="assignmentEnd"
							v-model="windowEnd"
							type="datetime-local"
							class="form-control form-control-sm"
						/>
					</div>
					<!-- Time on site (§5.2): the day board and the drive plan add it to
					     the drive legs, so it is stated with the window it sits in.
					     Blank is "no override" — the district's per-kind default
					     applies — which is why an empty field is never sent as 0. -->
					<div class="col-12">
						<label class="form-label" for="assignmentServiceMinutes">
							Service time (minutes)
							<span class="text-muted small ms-1">optional</span>
						</label>
						<input
							id="assignmentServiceMinutes"
							v-model="serviceMinutes"
							type="number"
							min="1"
							max="720"
							step="1"
							class="form-control form-control-sm"
							placeholder="Leave blank for the district's default for this kind"
						/>
					</div>
				</div>

				<!-- For `custom` the text IS the specification of the work (§7
				     extension, operator decision 1), so the field is marked and drawn
				     as required; every other kind carries its instruction in its name
				     and the text is a note. -->
				<div class="mt-2">
					<label
						class="form-label"
						:class="{ 'fw-bold': isCustom }"
						for="assignmentInstructions"
					>
						Instructions
						<span v-if="isCustom" class="badge bg-warning text-dark ms-1">
							Required for a custom assignment
						</span>
						<span v-else class="text-muted small ms-1">optional</span>
					</label>
					<textarea
						id="assignmentInstructions"
						v-model="instructions"
						class="form-control form-control-sm"
						:class="{ 'border-warning border-2': isCustom }"
						rows="2"
						:required="isCustom"
						placeholder="What the technician is asked to do, and anything they need to know on arrival."
					></textarea>
				</div>

				<!-- A Trap Configuration is the instruction a `set_trap` carries, and
				     only that kind may name one (platform/assignment.go's
				     ValidateAssignmentCreate). The district's configurations are the
				     same read the Configurations page uses; a retired family is not
				     offered for new work. -->
				<div v-if="kind === 'set_trap'" class="row g-2 mt-1">
					<div class="col-12">
						<label class="form-label" for="assignmentTrapConfiguration">
							Trap configuration
						</label>
						<select
							id="assignmentTrapConfiguration"
							v-model.number="trapConfigurationId"
							class="form-select form-select-sm"
						>
							<option :value="0">None stated</option>
							<option
								v-for="configuration in liveConfigurations"
								:key="configuration.id"
								:value="configuration.id"
							>
								{{ configuration.name }} (v{{ configuration.version }})
							</option>
						</select>
					</div>
				</div>

				<!-- The office has no Feature or Facilitator picker, so the two ids
				     survive behind a collapsed disclosure: a target the map cannot
				     show is still a target an operator may have to state, and it is
				     the one alternative to picking. In bulk mode the checked rows
				     are the targets, so there is nothing to state here. -->
				<details v-if="!bulk" class="mt-2">
					<summary class="small text-body-secondary">
						State an id instead
					</summary>
					<div class="row g-2 mt-1">
						<div class="col-12">
							<label class="form-label" for="assignmentFeature"
								>Feature #</label
							>
							<div class="input-group input-group-sm">
								<input
									id="assignmentFeature"
									v-model.number="targetFeatureId"
									type="number"
									min="1"
									class="form-control"
									@change="lookupFeature"
								/>
								<button
									type="button"
									class="btn btn-outline-secondary"
									:disabled="featureChecking"
									@click="lookupFeature"
								>
									Check
								</button>
							</div>
							<p class="form-text mb-0">
								{{
									featureNote ||
									"The id is checked against the Features ledger."
								}}
							</p>
						</div>
						<div class="col-12">
							<label class="form-label" for="assignmentFacilitator">
								Facilitator #
							</label>
							<input
								id="assignmentFacilitator"
								v-model.number="targetFacilitatorId"
								type="number"
								min="1"
								class="form-control form-control-sm"
							/>
							<p class="form-text mb-0">
								Facilitators are readable on the device pull only, so the id is
								stated.
							</p>
						</div>
					</div>
				</details>

				<div v-if="formError" class="alert alert-danger py-2 small mt-2">
					{{ formError }}
				</div>

				<div class="d-flex justify-content-end mt-2">
					<ButtonLoading
						type="submit"
						:text="submitLabel"
						icon="bi bi-plus-lg"
						variant="primary"
						:loading="saving"
						:disabled="saving"
					/>
				</div>
			</form>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { formatDate } from "@/format";
import type {
	AssignmentCreate,
	AssignmentKind,
	MapReviewFeatureRow,
	MapReviewQueryEnvelope,
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	OperationsWorkRow,
	ParcelResolveResponse,
	SiteCreateRequest,
	SiteCreateResponse,
	SyncAssignment,
	TrapConfigurationResult,
	User,
} from "@/type/api";
import type { GeoJSON } from "geojson";

import type { MapPickTarget, TargetDrawMode } from "./model";
import {
	defaultWindow,
	describeError,
	KIND_OPTIONS,
	originBadge,
	siteLabel,
	wireInstant,
} from "./wire";
import type { WorkRefusal, WorkTarget } from "./work-targets";
import { resolveWorkTargets } from "./work-targets";

interface Props {
	users: User[];
	sites: MapReviewSiteRow[];
	trapConfigurations: TrapConfigurationResult[];
	targets: MapPickTarget[];
	pickMode: TargetDrawMode;
	/** The Work rows the ledger has checked: the targets of the bulk door. */
	work: OperationsWorkRow[];
	/** Whether the operator asked for one assignment per checked Work row
	 * instead of authoring against map targets. The page owns it, because the
	 * map's picking has to be off while it is on. */
	bulk: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	notify: [title: string, message: string];
	/** The whole submit landed. */
	created: [assignments: SyncAssignment[]];
	/** Some of the submit landed: the page records those rows, and the form
	 * stays open holding the ones that did not. */
	written: [assignments: SyncAssignment[]];
	"update:targets": [targets: MapPickTarget[]];
	"update:pickMode": [mode: TargetDrawMode];
	"update:bulk": [bulk: boolean];
}>();

/** The three ways a target is chosen: clicking the objects already on the map,
 * placing a point, or drawing an area. The mode is the map's instruction, so it
 * lives on the page rather than inside this form. */
const PICK_MODES: {
	value: TargetDrawMode;
	label: string;
	icon: string;
	hint: string;
}[] = [
	{
		value: "pick",
		label: "Pick",
		icon: "bi bi-hand-index",
		hint: "Click a Site, a trap station or a parcel on the map. Each click adds one target.",
	},
	{
		value: "point",
		label: "Point",
		icon: "bi bi-crosshair",
		hint: "Click the map to place a point target. A second point replaces it.",
	},
	{
		value: "area",
		label: "Area",
		icon: "bi bi-bounding-box",
		hint: "Click the area's corners, then double-click or press Enter to close it. A second area replaces it.",
	},
];

const root = ref<HTMLElement | null>(null);

const kind = ref<AssignmentKind>("inspect");
const instructions = ref("");
const technicianId = ref(0);
const initialWindow = defaultWindow();
const windowStart = ref(initialWindow.start);
const windowEnd = ref(initialWindow.end);
// The stop's planned time on site, as the input's own text: blank must stay
// distinguishable from 0, because blank is "no override" and 0 is refused by
// the create door (platform/assignment.go's range check mirrors the schema).
const serviceMinutes = ref("");
const trapConfigurationId = ref(0);

// The disclosure's alternative to a picked target. These are the ids the API's
// Feature and Facilitator targets take; they are used only when the operator
// picked nothing on the map.
const targetFeatureId = ref(0);
const targetFacilitatorId = ref(0);

const featureNote = ref("");
const featureChecking = ref(false);
const prefillNote = ref("");

const saving = ref(false);
const formError = ref("");

/** The checked rows as the assignments they would become. */
const bulkTargets = ref<WorkTarget[]>([]);
const bulkRefusals = ref<WorkRefusal[]>([]);
const bulkError = ref("");
const bulkReading = ref(false);

const isCustom = computed(() => kind.value === "custom");

/** The checked rows an assignment can be authored from. Direct work is already
 * an assignment, so it is not a candidate rather than a candidate that failed. */
const eligibleWork = computed(() =>
	props.work.filter((row) => row.origin !== "direct"),
);

let bulkToken = 0;

// The rows are read whenever the door is on or the checked set changes: a
// footprint is not on the queued row, so each one is a read of its own. A newer
// set supersedes an older read, so a slow answer cannot write rows the operator
// has since unchecked.
watch(
	() => [props.bulk, props.work] as const,
	async ([bulk]) => {
		const token = ++bulkToken;
		bulkTargets.value = [];
		bulkRefusals.value = [];
		bulkError.value = "";
		bulkReading.value = false;
		if (!bulk) return;
		bulkReading.value = true;
		try {
			const resolved = await resolveWorkTargets(props.work);
			if (token !== bulkToken) return;
			bulkTargets.value = resolved.targets;
			bulkRefusals.value = resolved.refusals;
		} catch (e: unknown) {
			if (token === bulkToken) bulkError.value = describeError(e);
		} finally {
			if (token === bulkToken) bulkReading.value = false;
		}
	},
	{ immediate: true },
);

const pickHint = computed(
	() =>
		PICK_MODES.find((mode) => mode.value === props.pickMode)?.hint ??
		PICK_MODES[0].hint,
);

const submitLabel = computed(() => {
	const count = props.bulk ? bulkTargets.value.length : props.targets.length;
	if (count > 1) {
		return `Create ${count} assignments`;
	}
	return "Create assignment";
});

// The create door refuses a technician who is not a live member of the
// district, so the select offers the live ones; the queue's labels still read
// every user, because a settled Assignment may name someone since deactivated.
const technicians = computed(() =>
	props.users.filter((user) => user.is_active),
);

const liveConfigurations = computed(() =>
	props.trapConfigurations.filter((configuration) => !configuration.retired),
);

function setTargets(next: MapPickTarget[]) {
	emit("update:targets", next);
}

function setPickMode(next: TargetDrawMode) {
	emit("update:pickMode", next);
}

function setBulk(next: boolean) {
	emit("update:bulk", next);
}

/** A target in the words the chips and the submit's report print. A parcel is
 * named by its assessor id because that is what the operator clicked and what
 * the resolve call is keyed on. */
function targetText(target: MapPickTarget): string {
	switch (target.kind) {
		case "site": {
			const site = props.sites.find((row) => row.id === target.siteId);
			return site ? siteLabel(site) : `Site #${target.siteId}`;
		}
		case "parcel":
			return `Parcel ${target.externalId} (${target.region})`;
		case "point": {
			const [lng, lat] = target.geometry.coordinates;
			return `Point ${lng.toFixed(5)}, ${lat.toFixed(5)}`;
		}
		case "area":
			return "Drawn area";
	}
}

function targetIcon(target: MapPickTarget): string {
	switch (target.kind) {
		case "site":
			return "bi-geo-alt";
		case "parcel":
			return "bi-bounding-box";
		case "point":
			return "bi-crosshair";
		case "area":
			return "bi-pentagon";
	}
}

function removeTarget(index: number) {
	setTargets(props.targets.filter((_, at) => at !== index));
}

// The Feature target is stated by id because the office has no Feature list
// read: the Features ledger's QUERY is the read that exists, and it answers one
// row for an id (the identity property's only operator is `equal`). The check is
// therefore the operator's confirmation that the id names work in this district,
// not a picker's substitute.
async function lookupFeature() {
	featureNote.value = "";
	if (!targetFeatureId.value) return;
	featureChecking.value = true;
	try {
		const envelope = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<MapReviewFeatureRow>
		>("/api/feature", {
			limit: 1,
			filter: [
				{ property: "id", operator: "equal", value: targetFeatureId.value },
			],
		});
		const feature = envelope.items[0];
		featureNote.value = feature
			? `Feature #${feature.id} — ${feature.type || "unclassified"} at ${feature.site}.`
			: `No feature #${targetFeatureId.value} in this district.`;
	} catch (e: unknown) {
		emit("notify", "Feature check failed", describeError(e));
	} finally {
		featureChecking.value = false;
	}
}

// The form keeps the technician and the window after a save: the office authors
// a day's work for one person in one sitting, and re-stating both for each row
// would be re-typing rather than deciding. The targets do not survive, because
// they were the work that was just written.
function resetForm() {
	kind.value = "inspect";
	instructions.value = "";
	trapConfigurationId.value = 0;
	targetFeatureId.value = 0;
	targetFacilitatorId.value = 0;
	featureNote.value = "";
	prefillNote.value = "";
	formError.value = "";
	setTargets([]);
	setPickMode("pick");
}

// A trap's details' one action: pre-fill the form from an open
// instance and let the operator submit. The instance's own point is the target
// when it has one, because that is the place the completion gate measures the
// open instance against (§P3.1 rule 3) — the target is what lets a later
// completion resolve the instance it closed. Without coordinates the instance's
// Site is the narrowing, which the gate still reads through `target_site_id`.
function startCollect(instance: MapReviewTrapInstanceRow) {
	resetForm();
	kind.value = "collect_trap";
	technicianId.value = 0;
	const here =
		instance.latitude !== null && instance.longitude !== null
			? ` at ${instance.latitude}, ${instance.longitude}`
			: "";
	const station = instance.station ? `${instance.station}, ` : "";
	prefillNote.value =
		`Pre-filled from open trap instance #${instance.id} (${station}` +
		`${instance.site}, set ${formatDate(new Date(instance.date))})${here}. ` +
		"Nothing is created until you choose a technician and submit.";
	if (instance.latitude !== null && instance.longitude !== null) {
		setTargets([
			{
				kind: "point",
				geometry: {
					type: "Point",
					coordinates: [instance.longitude, instance.latitude],
				},
			},
		]);
	} else {
		setTargets([{ kind: "site", siteId: instance.site_id }]);
	}
	root.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * The technician details' "Assign work" action: open the form already for that
 * person, so the operator states the work rather than re-stating who it is for.
 * The form keeps its window and clears anything a previous open left behind.
 */
function startForTechnician(forTechnicianId: number) {
	resetForm();
	if (forTechnicianId > 0) technicianId.value = forTechnicianId;
	root.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * A station's "Set trap here" action. A station is not an assignment target —
 * the work is placed at the Site it stands on, which is the one place the
 * completion gate can later resolve an instance through — so the kind and the
 * target are stated and the operator supplies the technician.
 */
function startForSite(siteId: number) {
	resetForm();
	kind.value = "set_trap";
	setTargets([{ kind: "site", siteId }]);
	const site = props.sites.find((row) => row.id === siteId);
	prefillNote.value =
		`Pre-filled as a trap deployment at ${site ? siteLabel(site) : `site #${siteId}`}. ` +
		"Nothing is created until you choose a technician and submit.";
	root.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** True for the refusal `POST /api/site` gives when the parcel already carries a
 * Site — the one case where the resolve call is the answer instead. */
function isConflict(e: unknown): boolean {
	return (
		typeof e === "object" &&
		e !== null &&
		"response" in e &&
		(e as { response?: { status?: number } }).response?.status === 409
	);
}

/**
 * The Site a parcel target resolves to. A parcel is not an assignment target
 * either: the work is placed at the Site standing on it, which is what the
 * completion gate resolves an instance through. So the Site is created from the
 * clicked point and the parcel's own identity, and when the server refuses
 * because the parcel already carries a Site, that Site is read back
 * (`/api/municipal/parcel-resolve`) rather than invented. Mirrors the Stations
 * page's parcel flow (StationPickerModal).
 */
async function siteForParcel(
	target: Extract<MapPickTarget, { kind: "parcel" }>,
): Promise<number> {
	try {
		const body: SiteCreateRequest = {
			latitude: target.lat,
			longitude: target.lng,
			region: target.region,
			external_id: target.externalId,
		};
		const created = await apiClient.JSONPost<SiteCreateResponse>(
			"/api/site",
			body,
		);
		return created.site.id;
	} catch (e: unknown) {
		if (!isConflict(e)) throw e;
		const resolved = await apiClient.JSONGet<ParcelResolveResponse>(
			`/api/municipal/parcel-resolve/${encodeURIComponent(
				target.externalId,
			)}?region=${encodeURIComponent(target.region)}`,
		);
		const siteId = resolved.site_ids?.[0];
		if (siteId === undefined) throw e;
		return siteId;
	}
}

/** The fields every Assignment in this submit shares. */
function baseBody(): AssignmentCreate {
	const body: AssignmentCreate = {
		technician_id: technicianId.value,
		creation_mode: "planned",
		kind: kind.value,
		instructions:
			instructions.value.trim() === "" ? null : instructions.value.trim(),
		start_time: wireInstant(windowStart.value),
		end_time: wireInstant(windowEnd.value),
	};
	if (kind.value === "set_trap" && trapConfigurationId.value > 0) {
		body.trap_configuration_id = trapConfigurationId.value;
	}
	const minutes = statedServiceMinutes();
	if (minutes !== null) body.service_minutes = minutes;
	return body;
}

/** The service time the operator stated, or null when the field is blank. Blank
 * is "no override", so the key is left off the body entirely rather than sent as
 * 0 — which the create door refuses, and which the plan would read as a stop
 * with no dwell instead of a stop whose time comes from the district default. */
function statedServiceMinutes(): number | null {
	const raw = serviceMinutes.value.trim();
	if (raw === "") return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
}

/** One checked Work row as the Assignment it becomes: the shared fields, the
 * row's own provenance, and its whole footprint as the target. The instructions
 * are the shared text where the operator stated one, else the row's own title —
 * the work's own description, which a `custom` assignment requires. */
function bulkBody(
	row: OperationsWorkRow,
	geometry: GeoJSON.MultiPolygon,
	shared: string,
): AssignmentCreate {
	const body = baseBody();
	body.target_geometry = JSON.stringify(geometry);
	body.instructions = shared !== "" ? shared : row.title.trim() || null;
	if (row.origin === "lead") {
		body.lead_id = row.id;
	} else {
		body.work_order_id = row.id;
	}
	return body;
}

/** What stops the bulk door, or the empty string. A checked row that names no
 * place, or a read still in flight, stops the whole batch: a write holding
 * fewer assignments than the operator checked would be worse than a refusal
 * they can act on. */
function bulkValidationError(): string {
	if (bulkReading.value) {
		return "Still reading the checked rows' H3 footprints.";
	}
	if (bulkError.value !== "") {
		return bulkError.value;
	}
	if (bulkRefusals.value.length > 0) {
		return bulkRefusals.value
			.map(
				(refusal) =>
					`#${refusal.row.id} ${refusal.row.title} ${refusal.reason}.`,
			)
			.join("\n");
	}
	if (bulkTargets.value.length === 0) {
		return "Check at least one Work row in the ledger to author an assignment for each.";
	}
	const minutes = statedServiceMinutes();
	if (
		minutes !== null &&
		(!Number.isInteger(minutes) || minutes < 1 || minutes > 720)
	) {
		return "Service time is a whole number of minutes between 1 and 720, or blank.";
	}
	if (isCustom.value && instructions.value.trim() === "") {
		const untitled = bulkTargets.value.find(
			({ row }) => row.title.trim() === "",
		);
		if (untitled) {
			return `A custom assignment needs instructions: state them here, or give row #${untitled.row.id} a title.`;
		}
	}
	return "";
}

/** One picked target as the create door's target fields. The Site a parcel
 * resolves to is written first, because it is the one target whose resolution
 * can fail. */
async function bodyForTarget(target: MapPickTarget): Promise<AssignmentCreate> {
	const body = baseBody();
	switch (target.kind) {
		case "site":
			body.target_site_id = target.siteId;
			return body;
		case "parcel":
			body.target_site_id = await siteForParcel(target);
			return body;
		case "point":
		case "area":
			body.target_geometry = JSON.stringify(target.geometry);
			return body;
	}
}

/** The one Assignment a stated Feature or Facilitator id names, when no target
 * was picked. */
function statedBody(): AssignmentCreate {
	const body = baseBody();
	if (targetFeatureId.value > 0) body.target_feature_id = targetFeatureId.value;
	else body.target_facilitator_id = targetFacilitatorId.value;
	return body;
}

function statedLabel(): string {
	return targetFeatureId.value > 0
		? `Feature #${targetFeatureId.value}`
		: `Facilitator #${targetFacilitatorId.value}`;
}

/** What is wrong with the form as it stands, or the empty string. */
function validationError(): string {
	if (technicianId.value <= 0) {
		return "Choose the technician the work is for.";
	}
	if (props.bulk) return bulkValidationError();
	if (isCustom.value && instructions.value.trim() === "") {
		return "A custom assignment needs instructions describing the work.";
	}
	const statedFeature = targetFeatureId.value > 0;
	const statedFacilitator = targetFacilitatorId.value > 0;
	if (statedFeature && statedFacilitator) {
		return "State either a feature or a facilitator, not both.";
	}
	if (props.targets.length > 0 && (statedFeature || statedFacilitator)) {
		return (
			"Clear the stated id, or remove the picked targets: an assignment has " +
			"exactly one target, and a stated id is the alternative to picking."
		);
	}
	if (props.targets.length === 0 && !statedFeature && !statedFacilitator) {
		return "Pick at least one target on the map, or state a feature or facilitator id.";
	}
	const minutes = statedServiceMinutes();
	if (
		minutes !== null &&
		(!Number.isInteger(minutes) || minutes < 1 || minutes > 720)
	) {
		return "Service time is a whole number of minutes between 1 and 720, or blank.";
	}
	return "";
}

/**
 * One Assignment per target — the picked map targets, or the checked Work rows
 * in bulk mode, in the order they were stated. Both phases are separated so a
 * parcel that resolves to no Site fails the submit before anything is written: a
 * partial write that silently dropped a target would be worse than a refusal the
 * operator can act on. Once every target resolves, the writes themselves may
 * still fail one at a time, and each outcome is reported by the target's own
 * label.
 */
async function submit() {
	formError.value = "";
	const invalid = validationError();
	if (invalid !== "") {
		formError.value = invalid;
		return;
	}

	saving.value = true;
	try {
		const planned: {
			label: string;
			/** The picked target's slot, when this row is a map target. */
			targetIndex: number | null;
			/** The checked row's slot, when this row is a Work row. */
			bulkIndex: number | null;
			body: AssignmentCreate;
		}[] = [];
		try {
			if (props.bulk) {
				const shared = instructions.value.trim();
				for (const [index, { row, geometry }] of bulkTargets.value.entries()) {
					planned.push({
						label: `#${row.id} ${row.title}`,
						targetIndex: null,
						bulkIndex: index,
						body: bulkBody(row, geometry, shared),
					});
				}
			} else if (props.targets.length > 0) {
				for (const [index, target] of props.targets.entries()) {
					planned.push({
						label: targetText(target),
						targetIndex: index,
						bulkIndex: null,
						body: await bodyForTarget(target),
					});
				}
			} else {
				planned.push({
					label: statedLabel(),
					targetIndex: null,
					bulkIndex: null,
					body: statedBody(),
				});
			}
		} catch (e: unknown) {
			formError.value = `Nothing was created: ${describeError(e)}`;
			emit("notify", "Assignments not created", formError.value);
			return;
		}

		const written: SyncAssignment[] = [];
		const writtenIndexes = new Set<number>();
		const writtenRows = new Set<number>();
		const failures: { label: string; message: string }[] = [];
		for (const row of planned) {
			try {
				const created = await apiClient.JSONPost<SyncAssignment>(
					"/api/assignment",
					row.body,
				);
				written.push(created);
				if (row.targetIndex !== null) writtenIndexes.add(row.targetIndex);
				if (row.bulkIndex !== null) writtenRows.add(row.bulkIndex);
			} catch (e: unknown) {
				failures.push({ label: row.label, message: describeError(e) });
			}
		}

		if (failures.length === 0) {
			emit(
				"notify",
				written.length === 1 ? "Assignment created" : "Assignments created",
				written.length === 1
					? `Assignment #${written[0].id} is draft work for technician #${written[0].technicianId}.`
					: `${written.length} draft assignments for technician #${written[0].technicianId}: ${written
							.map((row) => `#${row.id}`)
							.join(", ")}.`,
			);
			resetForm();
			emit("created", written);
			return;
		}

		// A partial write is reported against the targets themselves: the rows
		// that landed are gone from the chips (and, in bulk mode, from the row
		// list), so a retry submits exactly the ones that failed.
		if (writtenIndexes.size > 0) {
			setTargets(
				props.targets.filter((_, index) => !writtenIndexes.has(index)),
			);
		}
		if (writtenRows.size > 0) {
			bulkTargets.value = bulkTargets.value.filter(
				(_, index) => !writtenRows.has(index),
			);
		}
		formError.value =
			`Created ${written.length} of ${planned.length} assignments.\n` +
			failures.map((row) => `${row.label} — ${row.message}`).join("\n");
		emit(
			"notify",
			"Some assignments not created",
			`${written.length} of ${planned.length} created; ${failures.length} failed.`,
		);
		if (written.length > 0) emit("written", written);
	} finally {
		saving.value = false;
	}
}

defineExpose({ startCollect, startForSite, startForTechnician });
</script>

<style scoped>
.target-chip {
	font-size: 0.78rem;
	max-width: 100%;
}

.target-chip > span {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.target-chip-remove {
	font-size: 0.5rem;
}

/* A partial-failure report names one failed target per line. */
.alert-danger {
	white-space: pre-line;
}
</style>
